import { Controller } from '@hotwired/stimulus'

// I-UX-MAILED-01: the e-mail body edited as the formatted mail. The frame (sandboxed, no scripts) shows the mail's
// wrapper with an editable element (#email-editable) filled from the <textarea> (source), which stays the submitted
// field. Only a real edit writes the element's HTML back to the source, so an untouched draft is sent byte for byte.
// Typing in „Upravit HTML“ re-renders the frame. Paste and drop insert plain text only: no Word/HTML junk.
export default class WysiwygController extends Controller<HTMLElement> {
  static override targets = ['frame', 'source']
  static override values = { linkPrompt: String }

  declare readonly frameTarget: HTMLIFrameElement
  declare readonly sourceTarget: HTMLTextAreaElement
  declare readonly linkPromptValue: string

  private editor: HTMLElement | null = null
  private writing = false

  override connect(): void {
    this.load() // the srcdoc may have loaded before the controller connected
  }

  load(): void {
    const editor = this.frameTarget.contentDocument?.getElementById('email-editable') ?? null
    if (!editor || editor === this.editor) return

    this.editor = editor
    this.fill()
    editor.addEventListener('input', () => this.change())
    editor.addEventListener('paste', (event) => this.insertText(event, event.clipboardData))
    editor.addEventListener('drop', (event) => this.insertText(event, event.dataTransfer))
    this.resize()
  }

  render(): void {
    if (this.writing || !this.editor) return
    this.fill()
    this.resize()
  }

  keepSelection(event: MouseEvent): void {
    event.preventDefault() // a toolbar click keeps the caret in the frame
  }

  format({ params }: { params: { command?: string } }): void {
    if (params.command) this.exec(params.command)
  }

  link(): void {
    const url = window.prompt(this.linkPromptValue, 'https://')?.trim()
    if (url) this.exec('createLink', url)
  }

  private fill(): void {
    if (!this.editor) return
    this.editor.innerHTML = this.sourceTarget.value // in the sandboxed document: scripts and handlers stay inert
    // a body ending in a table (inquiry [tabulka]) gets a line after it to type on; in the source only once edited
    if (this.editor.lastChild?.nodeName === 'TABLE') {
      const line = this.editor.ownerDocument.createElement('div') // an empty line as the browser makes it: <div><br></div>
      line.append(this.editor.ownerDocument.createElement('br'))
      this.editor.append(line)
    }
  }

  private exec(command: string, value?: string): void {
    if (!this.editor) return
    this.editor.focus()
    this.editor.ownerDocument.execCommand(command, false, value) // fires input → change
  }

  private insertText(event: Event, data: DataTransfer | null): void {
    event.preventDefault()
    const text = data?.getData('text/plain') ?? ''
    if (text !== '') this.editor?.ownerDocument.execCommand('insertText', false, text)
  }

  private change(): void {
    if (!this.editor) return
    this.writing = true
    this.sourceTarget.value = this.editor.innerHTML
    this.sourceTarget.dispatchEvent(new Event('input', { bubbles: true })) // live preview, dirty-form guard
    this.writing = false
    this.resize()
  }

  // The frame grows and shrinks with the text (its CSS min-height stays the floor). Measured at the editable element:
  // the mail wrappers have no doctype, and in quirks mode the page is never shorter than the frame.
  // ponytail: sized on load and edit; an image loading later may need a scroll inside the frame.
  private resize(): void {
    if (this.editor) this.frameTarget.style.height = `${this.editor.offsetTop + this.editor.offsetHeight + 24}px`
  }
}
